import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { APP_ENV } from '../services/api'
import { metaForRoute, shouldIndex } from './meta'

// 由这个 hook 托管的标签都打上这个属性，方便下次原地更新而不是越加越多。
const OWNED = 'data-seo'

function upsert(selector, create, attr, value) {
  let el = document.head.querySelector(selector)
  if (!el) {
    el = create()
    el.setAttribute(OWNED, '')
    document.head.appendChild(el)
  }
  el.setAttribute(attr, value)
  return el
}

const meta = (name, content) =>
  upsert(`meta[name="${name}"]`, () => {
    const el = document.createElement('meta')
    el.setAttribute('name', name)
    return el
  }, 'content', content)

const property = (prop, content) =>
  upsert(`meta[property="${prop}"]`, () => {
    const el = document.createElement('meta')
    el.setAttribute('property', prop)
    return el
  }, 'content', content)

/**
 * 把当前路由该有的 meta 写进 <head>。页面只管调用，写什么由 seoForRoute 决定
 * ——爬虫那条路（api/prerender.js）用的是同一个函数。
 *
 * 注意这只对 Google 这类会执行 JS 的爬虫有效。Facebook / Twitter / 微信
 * 抓取分享卡片时不跑 JS。
 *
 * @param {{product?: object}} [data] 需要异步数据才能算出来的部分（目前只有商品）
 */
export function useDocumentMeta(data) {
  const location = useLocation()
  const m = metaForRoute(location.pathname, data)

  // 依赖里放序列化后的值，避免调用方每次渲染新建对象导致无限更新
  const key = JSON.stringify(m)

  useEffect(() => {
    const indexable = shouldIndex(APP_ENV, m.noindex)

    document.title = m.title
    meta('description', m.description)
    meta('robots', indexable ? 'index, follow' : 'noindex, nofollow')

    property('og:type', m.type)
    property('og:site_name', 'p1ur')
    property('og:title', m.title)
    property('og:description', m.description)
    property('og:image', m.image)
    property('og:url', m.canonical)
    meta('twitter:card', 'summary_large_image')
    meta('twitter:title', m.title)
    meta('twitter:description', m.description)
    meta('twitter:image', m.image)

    // canonical 只在该被收录时给。给一个 noindex 页面写 canonical
    // 等于一边说「别收录我」一边说「我的正式地址是这个」，自相矛盾。
    const existing = document.head.querySelector('link[rel="canonical"]')
    if (indexable) {
      upsert('link[rel="canonical"]', () => {
        const el = document.createElement('link')
        el.setAttribute('rel', 'canonical')
        return el
      }, 'href', m.canonical)
    } else if (existing) {
      existing.remove()
    }
  }, [key]) // eslint-disable-line react-hooks/exhaustive-deps
}

export default useDocumentMeta
